import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "lcg_cookie_consent_v1";

const CookieConsentContext = createContext(null);

const defaultConsent = { necessary: true, analytics: false };

// Aplica el consentimiento a los servicios reales de la web.
// Actualmente el único servicio analítico presente es PostHog (Emergent),
// que se activa/desactiva según el consentimiento del usuario.
function applyConsent(consent) {
  try {
    const ph = typeof window !== "undefined" ? window.posthog : undefined;
    if (!ph || typeof ph.opt_in_capturing !== "function") return;
    if (consent.analytics) {
      ph.opt_in_capturing();
      if (typeof ph.startSessionRecording === "function") ph.startSessionRecording();
    } else {
      if (typeof ph.stopSessionRecording === "function") ph.stopSessionRecording();
      ph.opt_out_capturing();
    }
  } catch (e) {
    // No bloquear la app si el proveedor de analítica no está disponible.
    console.error("No se pudo aplicar el consentimiento de cookies a la analítica:", e);
  }
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null); // null => sin decisión previa
  const [ready, setReady] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch (e) {
      console.error("No se pudo leer el consentimiento de cookies almacenado:", e);
      stored = null;
    }
    if (stored && typeof stored.analytics === "boolean") {
      setConsent({ ...defaultConsent, ...stored });
      applyConsent(stored);
    } else {
      // Sin consentimiento: analítica desactivada por defecto.
      applyConsent(defaultConsent);
    }
    setReady(true);
  }, []);

  const persist = useCallback((next) => {
    const value = { ...defaultConsent, ...next, savedAt: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
      console.error("No se pudo guardar el consentimiento de cookies:", e);
    }
    setConsent(value);
    applyConsent(value);
    return value;
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true });
    setPrefsOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false });
    setPrefsOpen(false);
  }, [persist]);

  const savePreferences = useCallback(
    (prefs) => {
      persist({ necessary: true, analytics: !!prefs.analytics });
      setPrefsOpen(false);
    },
    [persist]
  );

  const openPreferences = useCallback(() => setPrefsOpen(true), []);
  const closePreferences = useCallback(() => setPrefsOpen(false), []);

  const value = useMemo(
    () => ({
      consent,
      ready,
      hasDecision: !!consent,
      showBanner: ready && !consent,
      prefsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [consent, ready, prefsOpen, acceptAll, rejectAll, savePreferences, openPreferences, closePreferences]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent debe usarse dentro de CookieConsentProvider");
  return ctx;
}
